import type { SchedulerPro } from "@bryntum/schedulerpro-thin";
import { calculateColorForAllocation } from "@laborchart-modules/common/dist/charts/processors/utils/gantt";
import { AssignmentBarColors } from "../prop-types";

type EventRecordWithGetData = {
   getData?: (key: string) => unknown;
   [key: string]: unknown;
};

export const parseTimeToHours = (timeString: string): number => {
   const [hours, minutes] = timeString.split(":").map(Number);
   return hours + minutes / 60;
};

export const getEventRecordData = (eventRecord: EventRecordWithGetData, key: string): unknown => {
   if (typeof eventRecord?.getData === "function") {
      return eventRecord.getData(key);
   }

   return eventRecord?.[key];
};

export const getDurationHours = (start: number, end: number): number => {
   if (end >= start) return end - start;
   return 24 - start + end;
};

export const getAssignmentBarColorHex = (
   eventRecord: EventRecordWithGetData,
   assignmentBarColor: AssignmentBarColors,
   paidShiftHours: number,
): string => {
   switch (assignmentBarColor) {
      case AssignmentBarColors.PROJECT:
         return (
            (getEventRecordData(eventRecord, "projectColor") as string) ||
            (getEventRecordData(eventRecord, "eventColor") as string) ||
            ""
         );
      case AssignmentBarColors.ALLOCATION_PERCENTAGE: {
         const percentAllocated = getEventRecordData(eventRecord, "percentAllocated");
         if (percentAllocated != null) {
            return calculateColorForAllocation(Number(percentAllocated));
         }

         const startTime = getEventRecordData(eventRecord, "startTime");
         const endTime = getEventRecordData(eventRecord, "endTime");
         if (startTime && endTime) {
            const start = parseTimeToHours(String(startTime));
            const end = parseTimeToHours(String(endTime));
            const duration = getDurationHours(start, end);
            const allocation = (duration / paidShiftHours) * 100;
            return calculateColorForAllocation(allocation);
         }

         return "";
      }
      case AssignmentBarColors.JOB_TITLES:
         return (getEventRecordData(eventRecord, "jobTitleColor") as string) || "";
      case AssignmentBarColors.OVERTIME:
         return getEventRecordData(eventRecord, "overtime") ? "#D92626" : "#40BF4A";
      case AssignmentBarColors.STATUS:
         return (getEventRecordData(eventRecord, "statusColor") as string) || "#000000";
      default:
         return (
            (getEventRecordData(eventRecord, "projectColor") as string) ||
            (getEventRecordData(eventRecord, "eventColor") as string) ||
            ""
         );
   }
};

export const getVisibleDateRangeCenterDate = (visibleDateRange?: {
   startDate?: Date;
   endDate?: Date;
}): Date | null => {
   const startDate = visibleDateRange?.startDate;
   const endDate = visibleDateRange?.endDate;
   if (!startDate || !endDate) return null;

   return new Date((startDate.getTime() + endDate.getTime()) / 2);
};

export const isWeekdayTick = (unit: string, startDate: Date): boolean =>
   unit !== "day" || (startDate.getDay() !== 6 && startDate.getDay() !== 0);

export const applyHideWeekendsToScheduler = (
   scheduler: SchedulerPro,
   hideWeekends: boolean,
   shouldRestoreViewport: boolean,
): void => {
   const centerDate = shouldRestoreViewport
      ? getVisibleDateRangeCenterDate((scheduler as any).visibleDateRange)
      : null;

   scheduler.element?.classList?.toggle("b-hide-weekends", hideWeekends);

   const applyTimeAxisChanges = () => {
      if (!scheduler.timeAxis) return;

      if (hideWeekends) {
         scheduler.timeAxis.filterBy?.((tick: { startDate: Date }) =>
            isWeekdayTick(String(scheduler.timeAxis.unit), tick.startDate),
         );
      } else {
         scheduler.timeAxis.clearFilters?.();
      }
   };

   if (typeof (scheduler as any).runWithTransition === "function") {
      (scheduler as any).runWithTransition(applyTimeAxisChanges);
   } else {
      applyTimeAxisChanges();
   }

   if (centerDate) {
      (scheduler as any).scrollToDate?.(centerDate, { block: "center" });
   }
};

export const applyRowHeightToScheduler = (scheduler: SchedulerPro, rowHeight: number): void => {
   const safeRowHeight = Math.min(45, Math.max(24, Number(rowHeight) || 24));
   const timeOffHeight = Math.max(safeRowHeight - 2, 16);
   const timeOffMarginTop = Math.max(Math.floor((safeRowHeight - timeOffHeight) / 2), 0);

   scheduler.rowHeight = safeRowHeight;
   scheduler.element?.style.setProperty("--scheduler-row-height", `${safeRowHeight}px`);
   scheduler.element?.style.setProperty("--scheduler-time-off-height", `${timeOffHeight}px`);
   scheduler.element?.style.setProperty("--scheduler-time-off-margin-top", `${timeOffMarginTop}px`);
};

export const getTimeOffLabel = (
   interval: {
      reason?: string;
      custom_reason?: string;
      customReason?: string;
      name?: string;
   } | null,
   translateTimeOffReason: (reason: string) => string,
): string => {
   if (!interval) return "";

   const reason = interval.reason;
   const customReason = interval.custom_reason || interval.customReason;
   if (reason && String(reason).toLowerCase() === "other") {
      return customReason || translateTimeOffReason(reason);
   }

   if (reason) {
      return translateTimeOffReason(reason);
   }

   return interval.name || "";
};
